import React, { useState, useEffect, useRef } from 'react';
import '../styles/CompanyProfile.css';

// Custom hook to detect when an element is in the viewport.
const useInView = (options) => {
    const ref = useRef(null);
    const [isInView, setIsInView] = useState(false);
    useEffect(() => {
        const node = ref.current; // Capture node for cleanup.
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsInView(true);
                observer.disconnect();
            }
        }, options);
        if (node) observer.observe(node);
        return () => { if (node) observer.unobserve(node); };
    }, [ref, options]);
    return [ref, isInView];
};


const StatCounter = ({ end, duration = 3000, suffix = '' }) => {
    const [count, setCount] = useState(0);
    const [ref, isInView] = useInView({ threshold: 0.5 });

    useEffect(() => {
        if (isInView) {
            let start = 0;
            const endValue = parseInt(end);
            if (start === endValue) return;

            // Determines how many steps the animation will have.
            // More steps = smoother animation.
            const totalSteps = Math.min(100, endValue); 
            const increment = Math.ceil(endValue / totalSteps);
            const timerDuration = Math.floor(duration / totalSteps);

            const timer = setInterval(() => {
                start += increment;
                if (start >= endValue) {
                    setCount(endValue);
                    clearInterval(timer);
                } else {
                    setCount(start);
                }
            }, timerDuration);

            return () => clearInterval(timer);
        }
    }, [end, duration, isInView]);

    return <h3 ref={ref} className="lhs-profile-highlight-title">{count.toLocaleString()}{suffix}</h3>;
};
// Card with 3D interactive hover effect.
const InteractiveCard = ({ children }) => {
    const handleMouseMove = (e) => {
        const card = e.currentTarget;
        const { left, top, width, height } = card.getBoundingClientRect();
        const x = e.clientX - left;
        const y = e.clientY - top;
        const rotateX = -((height / 2) - y) / 15;
        const rotateY = ((width / 2) - x) / 15;
        card.style.setProperty('--rotateX', `${rotateX}deg`);
        card.style.setProperty('--rotateY', `${rotateY}deg`);
    };
    const handleMouseLeave = (e) => {
        const card = e.currentTarget;
        card.style.setProperty('--rotateX', '0deg');
        card.style.setProperty('--rotateY', '0deg');
    };
    return <div className="lhs-profile-highlight-item" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>{children}</div>;
};

const TrustIcon = () => <svg className="lhs-profile-highlight-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="m9 12 2 2 4-4"></path></svg>;
const NationwideIcon = () => <svg className="lhs-profile-highlight-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>;
const ExperienceIcon = () => <svg className="lhs-profile-highlight-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/></svg>;

function CompanyProfile() {
  const profilePdfUrl = "https://laxmihospitalityservices.in/Laxmi_Hospitality_Services_Profile.pdf";
  
  // --- IMPORTANT ---
  // To skip a missing image, simply remove its page number from this array.
  // For example, to skip pages 3 and 8, the array would look like:
  // const existingPages = ['01', '02', '04', '05', '06', '07', '09', '10'];
  const existingPages = [
    '01', '02',  '05', '06', '07', '08', '09', '10'
  ];

  const profileImages = existingPages.map(pageNumber => 
    `/profile/Laxmi_hospitality_Services_Profile_page-${pageNumber}.jpg`
  );

  return (
    <div className="lhs-profile-wrapper">
      <div className="lhs-profile-header">
        <h2 className="lhs-profile-title">The Heart of Our Operation</h2>
      </div>

      <div className="lhs-profile-highlights-grid">
        <InteractiveCard>
          <ExperienceIcon />
          <StatCounter end={15} suffix="+" duration={3000} />
          <p className="lhs-profile-highlight-description">Years of Proven Expertise</p>
        </InteractiveCard>
        <InteractiveCard>
          <NationwideIcon />
          <StatCounter end={17500} suffix="+" duration={3000} />
          <p className="lhs-profile-highlight-description">Meals Served Daily</p>
        </InteractiveCard>
        <InteractiveCard>
          <TrustIcon />
          <h3 className="lhs-profile-highlight-title-static">Trusted Partner</h3>
          <p className="lhs-profile-highlight-description">Reliability at Scale</p>
        </InteractiveCard>
      </div>

      <div className="lhs-glimpse-container">
          <div className="lhs-glimpse-reel">
              {profileImages.map((src, index) => (
                  <img key={`page-${index}`} src={src} alt={`Laxmi Hospitality Profile Page ${index + 1}`} className="lhs-glimpse-page" />
              ))}
              {/* Duplicate the images for a seamless loop */}
              {profileImages.map((src, index) => (
                  <img key={`page-dup-${index}`} src={src} alt={`Laxmi Hospitality Profile Page ${index + 1}`} className="lhs-glimpse-page" />
              ))}
          </div>
      </div>

      <div className="lhs-profile-cta-container">
        <h3 className="lhs-profile-cta-highlight">
          The Blueprint to Our Success: How We Deliver Excellence at Scale.
        </h3>
        <a href={profilePdfUrl} download="Laxmi_Hospitality_Services_Profile.pdf" className="lhs-profile-download-btn" aria-label="Download our Company Profile" target="_blank" rel="noopener noreferrer">
          <span>Download Our Profile</span>
          <svg className="lhs-download-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        </a>
      </div>
    </div>
  );
}

export default CompanyProfile;